import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Target, AlertTriangle, CheckCircle2, Plus, Save, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const CATS = ['rent', 'salaries', 'raw_materials', 'utilities', 'equipment_maintenance', 'marketing', 'packaging', 'licenses', 'cleaning', 'insurance', 'petty_cash', 'other'];

function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

export default function BudgetTracker({ branchId, token, t, lang, onDataReady }) {
  const [comparison, setComparison] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/accounting/budgets/comparison?branch_id=${branchId || ''}&month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      setComparison(data);
      if (onDataReady) onDataReady(data);
    } catch { toast.error(t('common.failed')); }
    setLoading(false);
  }, [branchId, token, month, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;

  const totals = comparison?.totals || {};

  return (
    <div className="space-y-5" data-testid="budget-tracker-tab">
      {/* Month Picker + Add Budget */}
      <div className="flex flex-wrap items-center gap-3">
        <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-40" data-testid="budget-month" />
        <Button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="set-budget-btn">
          <Plus className="w-4 h-4 me-2" /> {t('budget.setBudget')}
        </Button>
      </div>

      {/* Totals */}
      {comparison && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('budget.totalBudget')}</p>
            <p className="text-xl font-bold text-foreground">{fmt(totals.budget)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('budget.totalActual')}</p>
            <p className="text-xl font-bold text-amber-500">{fmt(totals.actual)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('budget.remaining')}</p>
            <p className={`text-xl font-bold ${totals.remaining >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(totals.remaining)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('budget.usedPct')}</p>
            <p className={`text-xl font-bold ${totals.pct_used > 100 ? 'text-red-500' : totals.pct_used > 80 ? 'text-amber-500' : 'text-emerald-500'}`}>{totals.pct_used}%</p>
          </CardContent></Card>
        </div>
      )}

      {/* Items */}
      {comparison?.items?.length > 0 ? (
        <div className="space-y-3">
          {comparison.items.map(item => {
            const pct = item.pct_used;
            const barColor = pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500';
            return (
              <Card key={item.category} className={item.over_budget ? 'border-red-500/30' : ''} data-testid={`budget-row-${item.category}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{t(`accounting.${toCamelCase(item.category)}`)}</span>
                      {item.over_budget && <Badge className="bg-red-500/10 text-red-500 text-[10px] py-0">{t('budget.overBudget')}</Badge>}
                      {item.alert && !item.over_budget && <Badge className="bg-amber-500/10 text-amber-500 text-[10px] py-0">{t('budget.warning80')}</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-muted-foreground">{fmt(item.actual)} / {fmt(item.budget)}</span>
                      <span className={`font-bold ${pct > 100 ? 'text-red-500' : pct > 80 ? 'text-amber-500' : 'text-emerald-500'}`}>{pct}%</span>
                    </div>
                  </div>
                  {item.budget > 0 && (
                    <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12" data-testid="budget-empty">
          <Target className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">{t('budget.noBudgets')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('budget.noBudgetsDesc')}</p>
        </div>
      )}

      {/* Set Budget Form */}
      {showForm && <BudgetForm branchId={branchId} token={token} t={t} month={month} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchData(); }} />}
    </div>
  );
}

function BudgetForm({ branchId, token, t, month, onClose, onSaved }) {
  const [budgets, setBudgets] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/accounting/budgets?branch_id=${branchId}&month=${month}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const map = {};
        (res.data.budgets || []).forEach(b => { map[b.category] = b.monthly_budget; });
        setBudgets(map);
      }).catch(() => {});
  }, [branchId, token, month]);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [cat, amt] of Object.entries(budgets)) {
        if (amt > 0) {
          await axios.post(`${API}/api/accounting/budgets`, { branch_id: branchId, category: cat, monthly_budget: parseFloat(amt), month }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }
      toast.success(t('budget.saved'));
      onSaved();
    } catch { toast.error(t('common.failed')); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="budget-form-modal">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">{t('budget.setBudget')} - {month}</h2>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-3">
            {CATS.map(cat => (
              <div key={cat} className="flex items-center gap-3">
                <span className="text-sm w-32 text-foreground">{t(`accounting.${toCamelCase(cat)}`)}</span>
                <Input type="number" placeholder="0" value={budgets[cat] || ''}
                  onChange={e => setBudgets(prev => ({...prev, [cat]: e.target.value}))}
                  className="flex-1" data-testid={`budget-input-${cat}`} />
                <span className="text-xs text-muted-foreground w-12">{t('common.sar')}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="save-budgets-btn">
              <Save className="w-4 h-4 me-2" /> {t('common.save')}
            </Button>
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
